import React, { useCallback, useEffect, useState } from 'react';
import { secureFetch } from '../../supabaseService';

function formatMoney(value) {
  return Number(value || 0).toLocaleString('en-IN', { maximumFractionDigits: 2 });
}

function typeLabel(type) {
  const map = {
    no_show: 'No-show',
    abandonment: 'Abandonment',
    breakdown: 'Breakdown',
  };
  return map[type] || type || '—';
}

export default function OwnerDefaultApprovals({ onQueueChange }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [busyId, setBusyId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const data = await secureFetch('/api/admin/owner-defaults?status=pending');
      if (data?.success === false) throw new Error(data.message || 'Failed to load');
      const rows = Array.isArray(data?.items) ? data.items : [];
      setItems(rows);
      onQueueChange?.(rows.length);
    } catch (err) {
      setError(err?.message || 'Failed to load owner defaults');
      setItems([]);
      onQueueChange?.(0);
    } finally {
      setLoading(false);
    }
  }, [onQueueChange]);

  useEffect(() => {
    load();
  }, [load]);

  const handleApprove = async (row) => {
    if (busyId) return;
    setBusyId(row.id);
    setError('');
    try {
      const result = await secureFetch(`/api/admin/owner-defaults/${row.id}/approve`, {
        method: 'POST',
        body: JSON.stringify({}),
      });
      if (result?.success === false) throw new Error(result.message || 'Approve failed');
      await load();
    } catch (err) {
      setError(err?.message || 'Approve failed');
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div>
      {loading && <p className="m-0 text-[12px] text-slate-400">Loading owner defaults…</p>}
      {error && <p className="mb-2 text-[12px] text-red-400">{error}</p>}
      {!loading && items.length === 0 && (
        <p className="m-0 text-[12px] text-slate-500">No pending owner defaults.</p>
      )}
      <div className="flex flex-col gap-3">
        {items.map((row) => {
          const ref = row.bookings?.booking_ref || row.booking_id;
          const reporter = row.reporter?.name || row.reported_by || '—';
          const penaltyNet = Number(row.penalty_net || 0);
          const clientCredit = Number(row.total_client_credit || 0);
          return (
            <div
              key={row.id}
              className="rounded-lg border border-[#C9A84C]/25 bg-[#030810]/80 p-3 text-[12px] text-slate-300"
            >
              <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
                <span className="font-semibold text-[#C9A84C]">#{ref}</span>
                <span className="rounded-full border border-white/20 px-2 py-0.5 text-[10px] uppercase">
                  {row.status}
                </span>
              </div>
              <div className="grid grid-cols-1 gap-1 sm:grid-cols-2">
                <span>Type: {typeLabel(row.default_type)}</span>
                <span>Penalty (net): ₹{formatMoney(penaltyNet)}</span>
                <span>Client credit: ₹{formatMoney(clientCredit)}</span>
                <span>Reported by: {reporter}</span>
                <span>
                  At: {row.reported_at
                    ? new Date(row.reported_at).toLocaleString('en-IN')
                    : '—'}
                </span>
                {row.default_type === 'breakdown' && (
                  <span>Breakdown days: {row.breakdown_days ?? '—'}</span>
                )}
              </div>
              <div className="mt-2 flex justify-end">
                <button
                  type="button"
                  disabled={busyId === row.id}
                  onClick={() => handleApprove(row)}
                  className="rounded border border-[#C9A84C]/45 bg-[#C9A84C]/15 px-3 py-2 text-[11px] font-semibold text-[#C9A84C] disabled:opacity-50"
                >
                  {busyId === row.id ? 'Approving…' : 'Approve'}
                </button>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
